import { useEffect, useMemo, useRef } from 'react'
import { Linking, Pressable } from 'react-native'
import { ImagePlaceholder } from '../../../elements/images/ImagePlaceholder/ImagePlaceholder.js'
import { useMediaIs } from '../../../style/media.js'
import { zoomScale } from '../../content/FilmLandscapePoster/FilmLandscapePoster.constants.js'
import { ScaledImage } from '../../content/FilmLandscapePoster/FilmLandscapePoster.js'
import { AdInView } from '../AdPlacement.js'
import { useSkipListAd } from '../listAdInsertion/ListAdInsertionContext.js'
import { useBannerAdDecision } from '../useBannerAdDecision.js'

// Default size within the carousel
const dimensions = { width: 303, height: 167 }

export const PosterLandscapeAd = ({
  zone,
  placementXs,
  placementSm,
  index,
  listIndex,
  width,
  zoomed,
  onMouseMove,
}) => {
  const sm = useMediaIs('sm')

  const placement = (sm ? placementSm : placementXs) ?? placementSm ?? placementXs

  const { bannerData, loading } = useBannerAdDecision(zone, placement)

  const skipAd = useSkipListAd()

  useEffect(() => {
    if (!loading && bannerData?.message === 'no ads' && zone && placement) {
      skipAd(zone, placement, index, listIndex)
    }
  }, [loading, bannerData?.message, zone, placement, index, listIndex, skipAd])

  const adItem = useMemo(
    () =>
      bannerData && {
        type: 'ad',
        slug: zone + placement + index + listIndex,
        title: bannerData?.custom?.heading || bannerData?.title || 'Ad',
        description: bannerData.custom?.longDescription || bannerData.description,
        clickUrl: bannerData.clickUrl ?? '',
        posterImage: bannerData.images?.[0]?.url ?? '',
        playlist: bannerData.videos?.[0] && [
          {
            mime: 'video/mp4',
            ...bannerData.videos?.[0],
          },
        ],
        beacons: bannerData.beacons,
      },
    [bannerData, zone, placement, index, listIndex],
  )

  return (
    <PosterZoomedAdImage
      loading={loading}
      item={adItem}
      width={width}
      zoomed={zoomed}
      onMouseMove={onMouseMove}
    />
  )
}

export const PosterZoomedAdImage = ({
  item,
  loading,
  width: explicitWidth,
  zoomed,
  onMouseMove,
  disableImpressionTracking,
}) => {
  // Default size within the carousel
  const width = explicitWidth ?? dimensions.width
  const height = Math.ceil((width * dimensions.height) / dimensions.width)

  // Dimensions of the poster image when zoomed on hover
  const zoomedWidth = Math.ceil(width * zoomScale)
  const zoomedHeight = Math.ceil(height * zoomScale)

  const itemContainer = useRef()

  if (loading) {
    return <ImagePlaceholder width={width} height={height} />
  }

  return (
    <AdInView data={item} disabled={disableImpressionTracking}>
      <Pressable onPress={() => item?.clickUrl && Linking.openURL(item.clickUrl)}>
        <ImagePlaceholder
          ref={itemContainer}
          width={zoomed ? zoomedWidth : width}
          height={zoomed ? zoomedHeight : height}
          onMouseMove={
            onMouseMove &&
            item &&
            ((ev) =>
              itemContainer.current.measure((fx, fy, width, height, x, y) => {
                onMouseMove(ev, item, { x, y, width, height, zoomedWidth, zoomedHeight })
              }))
          }
        >
          {!!item.posterImage && (
            <ScaledImage
              alt={item.title}
              source={{ uri: item.posterImage }}
              width={zoomedWidth}
              height={zoomedHeight}
              scaledSize={zoomed ? false : { width, height }}
            />
          )}
        </ImagePlaceholder>
      </Pressable>
    </AdInView>
  )
}
